import { Lightbulb } from 'lucide-react'
import type { DealHealth, LeadScore, AccountHealth } from '@/types'
import { SEMANTIC, CATEGORICAL } from '@/lib/theme'

interface NextBestActionProps {
  type: 'opportunity' | 'lead' | 'account'
  health?: DealHealth | null
  leadScore?: LeadScore | null
  accountHealth?: AccountHealth | null
}

interface ActionResult {
  action: string
  score: number
}

interface Signal {
  score: number
  action: string
}

function findLowest(signals: Signal[]): Signal {
  let lowest = signals[0] as Signal
  for (let i = 1; i < signals.length; i++) {
    if ((signals[i] as Signal).score < lowest.score) {
      lowest = signals[i] as Signal
    }
  }
  return lowest
}

function getOpportunityAction(health: DealHealth): ActionResult | null {
  if (health.score >= 60) return null

  const signals: Signal[] = [
    { score: health.breakdown.activity.score, action: 'Schedule a follow-up call or meeting' },
    {
      score: health.breakdown.process.score,
      action: 'Complete discovery and budget qualification',
    },
    { score: health.breakdown.closeDate.score, action: 'Update close date to a realistic target' },
    {
      score: health.breakdown.stage.score,
      action: 'Advance to next stage or reassess deal viability',
    },
  ]

  const lowest = findLowest(signals)

  if (lowest.score < 10) {
    return { action: lowest.action, score: lowest.score }
  }

  if (health.score < 40) {
    return { action: 'Review deal strategy with manager', score: lowest.score }
  }

  return null
}

function getLeadAction(score: LeadScore): ActionResult | null {
  if (score.score >= 60) return null

  const signals: Signal[] = [
    { score: score.breakdown.engagement.score, action: 'Make an outreach call or send email' },
    { score: score.breakdown.profile.score, action: 'Research and complete lead profile' },
    { score: score.breakdown.source.score, action: 'Validate lead quality and source' },
    { score: score.breakdown.status.score, action: 'Advance lead status or disqualify' },
  ]

  const lowest = findLowest(signals)

  if (lowest.score < 10) {
    return { action: lowest.action, score: lowest.score }
  }

  if (score.score < 40) {
    return { action: 'Review lead priority and next steps', score: lowest.score }
  }

  return null
}

function getAccountAction(health: AccountHealth): ActionResult | null {
  if (health.score >= 60) return null

  const signals: Signal[] = [
    { score: health.breakdown.engagement.score, action: 'Schedule a check-in meeting or call' },
    {
      score: health.breakdown.revenue.score,
      action: 'Identify upsell or cross-sell opportunities',
    },
    {
      score: health.breakdown.renewal.score,
      action: 'Start renewal conversation before contract expires',
    },
    { score: health.breakdown.breadth.score, action: 'Expand contacts and deepen relationship' },
  ]

  const lowest = findLowest(signals)

  if (lowest.score < 10) {
    return { action: lowest.action, score: lowest.score }
  }

  if (health.score < 40) {
    return { action: 'Review account strategy with manager', score: lowest.score }
  }

  return null
}

function getUrgencyColor(score: number): {
  backgroundColor: string
  color: string
  borderColor: string
  iconColor: string
} {
  if (score < 5) {
    // High priority: error state (Raspberry fill + white text)
    return {
      backgroundColor: SEMANTIC.error.fill,
      color: SEMANTIC.error.text,
      borderColor: SEMANTIC.error.fill,
      iconColor: '#ffffff',
    }
  }
  if (score < 15) {
    // Medium priority: warning state (Pollen fill + Prussian text)
    return {
      backgroundColor: SEMANTIC.warning.fill,
      color: SEMANTIC.warning.text,
      borderColor: SEMANTIC.warning.fill,
      iconColor: CATEGORICAL.prussian.solid,
    }
  }
  // Low priority: neutral state (Pale Grey fill + Navy text)
  return {
    backgroundColor: SEMANTIC.neutral.fill,
    color: SEMANTIC.neutral.text,
    borderColor: CATEGORICAL.navy.solid,
    iconColor: CATEGORICAL.navy.solid,
  }
}

export function NextBestAction({ type, health, leadScore, accountHealth }: NextBestActionProps) {
  let result: ActionResult | null = null

  if (type === 'opportunity' && health) {
    result = getOpportunityAction(health)
  } else if (type === 'lead' && leadScore) {
    result = getLeadAction(leadScore)
  } else if (type === 'account' && accountHealth) {
    result = getAccountAction(accountHealth)
  }

  if (!result) return null

  const colors = getUrgencyColor(result.score)

  return (
    <div
      className="flex items-center gap-2 rounded-md border px-3 py-2"
      style={{
        backgroundColor: colors.backgroundColor,
        borderColor: colors.borderColor,
      }}
      role="status"
      aria-label={`Suggested next action: ${result.action}`}
    >
      <Lightbulb
        className="h-4 w-4 shrink-0"
        style={{ color: colors.iconColor }}
        aria-hidden="true"
      />
      <span className="text-xs font-medium" style={{ color: colors.color }}>
        {result.action}
      </span>
    </div>
  )
}
